import { Component, MouseEvent as ReactMouseEvent } from 'react'
import * as cn from 'classnames'
import * as debounce from 'lodash/debounce'
import { OpportunityCategory } from 'interfaces/opportunities'
import { doesSupportTouch } from 'helpers/styleHelpers'
import * as styles from 'components/features/insights/InsightCategoriesRibbon/InsightCategoriesRibbon.css'
import { OpportunityCategoryId } from 'interfaces/meta'
import { Counter } from 'components/features/pfm/Counter/Counter'
import SvgIcon from 'components/atoms/SvgIcon'
import ReactSvgIcon from 'icons/*.svg'
import { OPPORTUNITY_CATEGORY_IDS } from 'constants/opportunities'
import { formatCount } from 'helpers/formatHelpers'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { WithPlacement } from 'interfaces/insights'
import { OpportunityPlacement } from 'interfaces/opportunities'

type OpportunityCategoryIconMap = Record<OpportunityCategoryId, typeof ReactSvgIcon>

const opportunityCategoryIcons = OPPORTUNITY_CATEGORY_IDS.reduce<OpportunityCategoryIconMap>(
  (icons: OpportunityCategoryIconMap, opportunityCategoryId: OpportunityCategoryId) => {
    return {
      ...icons,
      [opportunityCategoryId]: require(`icons/${ opportunityCategoryId }.svg`).default,
    }
  },
  {} as OpportunityCategoryIconMap,
)

export type InsightCategoriesRibbonProps =
  & WithPlacement<OpportunityPlacement>
  & {
    theme?: 'regular' | 'white'
    categories: (OpportunityCategory)[]
    selectedCategoryId: OpportunityCategoryId
    onCategorySelect: (id: OpportunityCategoryId) => void
  }

type InsightCategoriesRibbonState = {
  rootNodeWidth: number
  scrollLeft: number
  isLongDistanceDragging: boolean
}

const IS_TOUCH_SCREEN = doesSupportTouch()

const CATEGORY_WIDTH = parseInt(styles.categoryWidth, 10)

export class InsightCategoriesRibbon extends Component<InsightCategoriesRibbonProps, InsightCategoriesRibbonState> {
  static defaultProps: Partial<InsightCategoriesRibbonProps> = {
    theme: 'regular',
  }

  state = {
    rootNodeWidth: Infinity,
    scrollLeft: 0,
    isLongDistanceDragging: false,
  }

  private rootNode: HTMLElement | null = null
  private scrollableNode: HTMLElement | null = null
  private dragStartClientX = 0
  private dragStartScrollLeft = 0
  private isDragging = false

  render () {
    const { categories, selectedCategoryId, theme } = this.props
    const selectedCategoryIndex = categories.findIndex((category) => category.id === selectedCategoryId)
    const contentWidth = this.getContentWidth()
    const { scrollLeft, rootNodeWidth } = this.state
    const isOverflown = contentWidth > rootNodeWidth
    const isLeftArrowHidden = !isOverflown || scrollLeft === 0
    const isRightArrowHidden = !isOverflown || scrollLeft + rootNodeWidth >= contentWidth
    const className = cn(styles.menu, styles[theme!], {
      [styles.dragging]: this.state.isLongDistanceDragging,
    })
    const highlighterStyle = {
      transform: `translateX(${ selectedCategoryIndex * 100 }%)`,
    }

    return (
      <nav
        className={ className }
        ref={ (node) => this.rootNode = node }
        onMouseDown={ this.onMouseDown }
      >
        <div
          className={ styles.scrollable }
          ref={ (node) => this.scrollableNode = node }
          onScroll={ this.handleGeometryChangeDebounced }
        >
          <div className={ styles.ribbon }>
            <div className={ styles.highlighter } style={ highlighterStyle } />
            { categories.map((category) => this.renderCategory(category)) }
          </div>
        </div>
        <div
          onClick={ this.onLeftArrowClick }
          className={ cn(styles.arrow, styles.left, { [styles.hidden]: isLeftArrowHidden }) }
        />
        <div
          onClick={ this.onRightArrowClick }
          className={ cn(styles.arrow, styles.right, { [styles.hidden]: isRightArrowHidden }) }
        />
      </nav>
    )
  }

  renderCategory (category: OpportunityCategory) {
    const { id, title } = category
    const { onCategorySelect, selectedCategoryId } = this.props
    const isSelected = category.id === selectedCategoryId
    const categoryClassName = cn(styles.category, isSelected && styles.selected)

    const counterWrapperChild = 'newOpportunitiesCount' in category
      ? (
        <div className={ styles.counterWrapper }>
          <Counter
            count={ formatCount(category.newOpportunitiesCount) }
            shape='round'
            theme='green'
          />
        </div>
      )
      : null

    return (
      <div data-qa-marker='opportunities-category-menu' key={ id }>
        <AnalyticsClick
          category='site_controls-opportunities_filter'
          action='select'
          label={ `${ selectedCategoryId } → ${ id }` }
        >
          <div
            className={ categoryClassName }
            onClick={ () => onCategorySelect(id) }
          >
            <div className={ styles.iconContainer }>
              <div className={ styles.icon }>
                <SvgIcon src={ opportunityCategoryIcons[id] } />
              </div>
              { counterWrapperChild }
            </div>
            <div className={ styles.textPositioner }>
              <div className={ styles.textWrapper }>
                { title }
              </div>
            </div>
          </div>
        </AnalyticsClick>
      </div>
    )
  }

  componentDidMount () {
    window.addEventListener('resize', this.handleGeometryChangeDebounced)
    this.handleGeometryChange()

    this.scrollSelectedInToView()
  }

  componentWillUnmount () {
    this.removeDocumentEventListeners()
    window.removeEventListener('resize', this.handleGeometryChangeDebounced)
    this.handleGeometryChangeDebounced.cancel()
  }

  private scrollSelectedInToView () {
    const { categories, selectedCategoryId } = this.props
    const selectedCategoryIndex = categories.findIndex((category) => category.id === selectedCategoryId)
    const scrollLeft = (selectedCategoryIndex - 1) * CATEGORY_WIDTH
    this.setScrollLeft(Math.max(0, scrollLeft))
  }

  private getContentWidth () {
    const { categories } = this.props
    return CATEGORY_WIDTH * categories.length
  }

  private setScrollLeft (newScrollLeft: number, isAnimatedTransition = false) {
    if (!this.scrollableNode) {
      return
    }

    if (isAnimatedTransition) {
      this.scrollableNode.scroll({
        top: 0,
        left: newScrollLeft,
        behavior: 'smooth',
      })
    } else {
      this.scrollableNode.scrollLeft = newScrollLeft
    }
  }

  private addDocumentEventListeners () {
    document.addEventListener('mousemove', this.onDocumentMouseMove)
    document.addEventListener('mouseup', this.onDocumentMouseUp)
    document.addEventListener('mouseleave', this.onDocumentMouseUp)
  }

  private removeDocumentEventListeners () {
    document.removeEventListener('mousemove', this.onDocumentMouseMove)
    document.removeEventListener('mouseup', this.onDocumentMouseUp)
    document.removeEventListener('mouseleave', this.onDocumentMouseUp)
  }

  private handleGeometryChange = () => {
    const { rootNode, scrollableNode } = this
    if (!rootNode || !scrollableNode || this.isDragging) {
      return
    }

    const rootNodeWidth = rootNode.offsetWidth
    const scrollLeft = scrollableNode.scrollLeft
    this.setState({ rootNodeWidth, scrollLeft })
  }

  private handleGeometryChangeDebounced = debounce(this.handleGeometryChange, 50)

  private onLeftArrowClick = () => {
    const { scrollLeft, rootNodeWidth } = this.state
    const contentWidth = this.getContentWidth()
    const scrollRight = contentWidth - scrollLeft - rootNodeWidth
    const newScrollRight = Math.floor(scrollRight / CATEGORY_WIDTH) * CATEGORY_WIDTH + CATEGORY_WIDTH
    const newScrollLeft = contentWidth - newScrollRight - rootNodeWidth
    this.setScrollLeft(newScrollLeft, true)
  }

  private onRightArrowClick = () => {
    const { scrollLeft } = this.state
    const newScrollLeft = Math.floor(scrollLeft / CATEGORY_WIDTH) * CATEGORY_WIDTH + CATEGORY_WIDTH
    this.setScrollLeft(newScrollLeft, true)
  }

  private onMouseDown = (event: ReactMouseEvent<HTMLElement>) => {
    const { scrollableNode } = this
    if (!scrollableNode || IS_TOUCH_SCREEN) {
      return
    }

    this.isDragging = true
    this.dragStartClientX = event.clientX
    this.dragStartScrollLeft = scrollableNode.scrollLeft
    this.addDocumentEventListeners()
  }

  private onDocumentMouseMove = (event: MouseEvent) => {
    const shift = this.dragStartClientX - event.clientX
    const newScrollLeft = this.dragStartScrollLeft + shift
    if (shift < -10 || shift > 10) {
      this.setState({ isLongDistanceDragging: true })
    }
    this.setScrollLeft(newScrollLeft)
  }

  private onDocumentMouseUp = () => {
    this.isDragging = false
    this.removeDocumentEventListeners()
    this.setState({ isLongDistanceDragging: false }, this.handleGeometryChange)
  }
}

